import config from "@~/envConfig";
import { Product, ProductListConfig } from "@~/types/types";
import { reportClientError } from "@~/utils/reportClientError";

export type SocialProofMessages = {
  productId: string;
  messages: {
    title: string;
    id: string;
    description: string;
  }[];
};

const API_URL = config.socialProofApi;

export default async function fetchSocialProofData(
  products: Product[],
  appId: string,
  configName: string | undefined,
  consumerId: string,
  instanceId?: string,
): Promise<SocialProofMessages[]> {
  if (!appId) {
    return Promise.resolve([]);
  }

  const extractAvailability = (product: Product) => {
    return (
      product.variants.length > 0 &&
      product.variants[0] &&
      product.variants[0].availability?.availableToOrder
    );
  };

  const productsData = products.map((product) => ({
    productId: product.aliases.pimProductId,
    brand: product.brand?.name,
    availableToOrder: extractAvailability(product) || false,
  }));

  const newrelicAttributes = {
    api: "socialProof",
    productIds: productsData.map(({ productId }) => productId).join(","),
    appId,
    config: configName,
    consumerId,
    instanceId,
  };

  let response: Response;
  try {
    response = await fetch(API_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ products: productsData, appId }),
    });
  } catch (error) {
    reportClientError(error, newrelicAttributes);
    throw error;
  }

  if (!response.ok) {
    const error = new Error(
      "Error fetching social proof messaging data " + response.status,
    );
    reportClientError(error, {
      ...newrelicAttributes,
      status: response.status,
    });
    throw error;
  }

  return response.json();
}

export const getSocialProofAppIdFromConfig = (
  config: ProductListConfig,
): string => {
  const socialProofComponent = (
    config["product-card-slots"].banner ||
    config["product-card-slots"]["image-banner"]
  )?.filter((bslot) => bslot.name === "SocialProofMessaging");

  return `${(socialProofComponent && socialProofComponent[0]?.props?.appId) || ""}`;
};

export const shouldFetchSocialProof = (config: ProductListConfig) => {
  return isComponentInConfig("SocialProofMessaging", config);
};

const isComponentInConfig = (
  componentName: string,
  config: ProductListConfig,
) => {
  const listOfComponentsInConfig = Object.values(
    config["product-card-slots"] ?? {},
  )
    .flat()
    .map(({ name }) => name);

  return listOfComponentsInConfig.includes(componentName);
};
